import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { Page, QuoteForm } from "../components/site";
import { PriceCalculator } from "../components/price-calculator";
import { Kostentabel } from "../components/kostentabel";
import { dienstFoto } from "../lib/plaats-foto";
import { algemeneFaq } from "../lib/algemene-faq";
import { SITE, dienstBySlug, diensten, euro, plaatsen } from "../lib/seo-data";

export const Route = createFileRoute("/elektricien/$dienst/")({
  loader: ({ params }) => {
    const dienst = dienstBySlug(params.dienst);
    if (!dienst) throw notFound();
    return { dienst };
  },
  head: ({ params, loaderData }) => {
    const d = loaderData?.dienst;
    if (!d) {
      return { meta: [{ title: "Pagina niet gevonden" }, { name: "robots", content: "noindex" }] };
    }
    const title = `${d.naam}: kosten ${d.prijs} | offerto`;
    const description = `Wat kost ${d.naam.toLowerCase()}? Gemiddeld ${euro(d.gemiddeld)}, meestal ${d.prijs}. Uurtarief ${d.uurtarief}. Bekijk het kostenoverzicht en vergelijk gratis offertes van erkende elektriciens.`;
    const url = `${SITE}/elektricien/${params.dienst}`;
    const foto = dienstFoto(d.slug, d.naam);
    const fotoUrl = `${SITE}${foto.url}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: fotoUrl },
        { property: "og:image:alt", content: foto.alt },
        { name: "twitter:image", content: fotoUrl },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: d.naam,
            areaServed: "Nederland",
            provider: { "@type": "Organization", name: "offerto" },
            description: d.intro,
            image: {
              "@type": "ImageObject",
              url: fotoUrl,
              width: 1200,
              height: 630,
              caption: foto.alt,
            },
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "EUR",
              lowPrice: d.van,
              highPrice: d.tot,
              offerCount: d.kosten.length,
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${url}#localbusiness`,
            name: "offerto",
            description: d.intro,
            url,
            areaServed: { "@type": "Country", name: "Nederland" },
            address: { "@type": "PostalAddress", addressCountry: "NL" },
            priceRange: "€€",
            currenciesAccepted: "EUR",
            image: fotoUrl,
            openingHoursSpecification: [
              {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                opens: "08:00",
                closes: "18:00",
              },
            ],
            makesOffer: {
              "@type": "Offer",
              url,
              itemOffered: { "@type": "Service", name: d.naam, serviceType: d.naam },
              priceSpecification: {
                "@type": "PriceSpecification",
                priceCurrency: "EUR",
                minPrice: d.van,
                maxPrice: d.tot,
              },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [...d.faq, ...algemeneFaq].map((f) => ({
              "@type": "Question",
              name: f.v,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Diensten", item: `${SITE}/diensten` },
              { "@type": "ListItem", position: 2, name: d.naam, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: DienstPagina,
});

function DienstPagina() {
  const { dienst } = Route.useLoaderData();
  const overige = diensten.filter((d) => d.slug !== dienst.slug);
  const foto = dienstFoto(dienst.slug, dienst.naam);

  return (
    <Page title={dienst.h1} intro={dienst.intro}>
      <nav className="mt-6 text-sm text-muted-foreground" aria-label="Kruimelpad">
        <Link to="/diensten" className="hover:text-foreground">
          Alle elektricienklussen en kosten
        </Link>
        <span className="px-2">/</span>
        <span className="text-foreground">{dienst.naam}</span>
      </nav>

      <figure className="mt-6 overflow-hidden rounded-lg border border-border bg-card">
        <img
          src={foto.url}
          alt={foto.alt}
          width={1200}
          height={630}
          fetchPriority="high"
          loading="eager"
          className="h-auto w-full object-cover"
        />
        <figcaption className="px-4 py-3 text-xs text-muted-foreground">
          {dienst.naam} door een aangesloten erkende elektricien.
        </figcaption>
      </figure>

      <div className="mt-6 rounded-lg border border-border bg-card p-5 sm:flex sm:items-center sm:justify-between sm:gap-6">
        <p className="text-sm text-muted-foreground">
          Vergelijk gratis en vrijblijvend tot 5 offertes van betrouwbare elektriciens voor {dienst.naam.toLowerCase()} bij jou in de buurt.
        </p>
        <Link
          to="/aanvraag"
          className="mt-4 inline-block rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground sm:mt-0 sm:shrink-0"
        >
          Gratis offertes aanvragen
        </Link>
      </div>



      <div className="mt-8 grid min-w-0 gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold">Wat doet de elektricien?</h2>
          <ul className="mt-4 space-y-2 text-muted-foreground">
            {dienst.werk.map((w) => (
              <li key={w} className="flex gap-2">
                <span className="text-primary">✓</span>
                {w}
              </li>
            ))}
          </ul>

          <Kostentabel dienst={dienst} />

          <PriceCalculator dienstSlug={dienst.slug} dienstNaam={dienst.naam} />

          <h2 className="mt-10 text-2xl font-bold">Veelgestelde vragen</h2>
          <dl className="mt-4 space-y-5">
            {dienst.faq.map((f) => (
              <div key={f.v}>
                <dt className="font-semibold">{f.v}</dt>
                <dd className="mt-1 text-muted-foreground">
                  {f.a}
                  {f.link && (
                    <>
                      {" "}
                      <Link to={f.link.to} className="font-semibold text-accent hover:underline">{f.link.label}</Link>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>

          <h2 className="mt-10 text-2xl font-bold">Een elektricien kiezen: kosten en betrouwbaarheid</h2>
          <dl className="mt-4 space-y-5">
            {algemeneFaq.map((f) => (
              <div key={f.v}>
                <dt className="font-semibold">{f.v}</dt>
                <dd className="mt-1 text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-sm text-muted-foreground">
            Meer antwoorden over certificeringen, reviews en rode vlaggen lees je op de pagina{" "}
            <Link to="/veelgestelde-vragen" className="font-semibold text-foreground hover:underline">
              veelgestelde vragen over het kiezen van een elektricien
            </Link>
            .
          </p>
        </div>

        <aside>
          <QuoteForm compact />
          <div className="mt-8 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold">Kosten van andere klussen</h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {overige.map((d) => (
                <li key={d.slug}>
                  <Link
                    to="/elektricien/$dienst"
                    params={{ dienst: d.slug }}
                    className="hover:text-foreground"
                  >
                    {d.naam}: kosten {d.prijs}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/diensten" className="font-medium text-primary hover:underline">
                  Alle elektricienklussen met prijzen
                </Link>
              </li>
            </ul>
          </div>
          <div className="mt-6 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold">Elektricien in jouw stad</h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {plaatsen.slice(0, 5).map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/plaatsen/$plaats"
                    params={{ plaats: p.slug }}
                    className="hover:text-foreground"
                  >
                    Elektricien {p.naam}: tarieven en voorrijkosten
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/plaatsen" className="font-medium text-primary hover:underline">
                  Alle plaatsen met lokale tarieven
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      <h2 className="mt-16 text-2xl font-bold">Kosten van {dienst.naam.toLowerCase()} per plaats</h2>
      <p className="mt-2 text-muted-foreground">
        Kies je plaats voor lokale richtprijzen en elektriciens in de buurt. Wil je eerst het
        volledige aanbod zien, bekijk dan{" "}
        <Link to="/diensten" className="font-medium text-primary hover:underline">
          alle elektricienklussen met kosten
        </Link>{" "}
        of{" "}
        <Link to="/plaatsen" className="font-medium text-primary hover:underline">
          de tarieven per plaats
        </Link>
        .
      </p>
      <ul className="mt-5 grid gap-2 text-sm sm:grid-cols-3 lg:grid-cols-4">
        {plaatsen.map((p) => (
          <li key={p.slug}>
            <Link
              to="/elektricien/$dienst/$plaats"
              params={{ dienst: dienst.slug, plaats: p.slug }}
              className="text-muted-foreground hover:text-primary"
            >
              {dienst.naam} in {p.naam}
            </Link>
          </li>
        ))}
      </ul>
    </Page>
  );
}
